import { Component, Inject } from '@angular/core';

import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { EventBusService } from '../../../../technical-modules/events/services/event-bus.service';

import { AccesAction, AccesEtat } from '../../../../app.constants';

import { AccesService } from '../services/acces.service';
import { AccesSearchResult } from '../entities/acces-search-result';
import { AccesListeGenericComponentBase } from './acces-liste-generic.component-base';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

type AccesSelection = {
	[accesId: number]: boolean;
};

type ExecutionClicked = {
	[executionId: number]: boolean;
};

type MassAction = {
	title: string,
	action: () => void,
	validation: (_acces: AccesSearchResult) => boolean,
	class: string,
	validClass: string,
	
	enabled: boolean,
};

@Component({
	selector: 'acces-liste-tarif-negatif',
	templateUrl: 'acces-liste-tarif-negatif.html',
	styleUrls: ['acces-liste.css'],
})
export class AccesTarifNegatifComponent extends AccesListeGenericComponentBase<AccesSearchResult[]> {
	allSelected: boolean = false;
	selected: AccesSelection = {};
	executionClicked: ExecutionClicked = {};
	
	// Definition des boutons pour les actions de masse
	private authorizeAction: MassAction = {
		title: 'Autoriser l\'accès',
		action: () => this.doAuthorize(),
		class: 'fa-regular fa-thumbs-up fa-lg',
		validation: (acces) => this.canAuthorizeAcces(acces),
		validClass: 'text-success',
		enabled: false,
	};
	private askValidationAction: MassAction = {
		title: 'Demander la validation',
		action: () => this.doAskValidation(),
		class: 'fa-regular fa-hand-pointer fa-lg',
		validation: (acces) => this.canAskValidationAcces(acces),
		validClass: '',
		enabled: false,
	};
	private denyAction: MassAction = {
		title: 'Refuser l\'accès',
		action: () => this.doDeny(),
		class: 'fa-regular fa-thumbs-down fa-lg',
		validation: (acces) => this.canDenyAcces(acces),
		validClass: 'text-active',
		enabled: false,
	};
	private closeAction: MassAction = {
		title: 'Fermer l\'accès',
		action: () => this.doClose(),
		class: 'fa-regular fa-hand fa-lg',
		validation: (acces) => this.canCloseAcces(acces),
		validClass: 'text-active',
		enabled: false,
	}
	private updateAction: MassAction = {
		title: 'Modifier l\'accès',
		action: () => this.doUpdate(),
		class: 'fa-regular fa-pen-to-square fa-lg',
		validation: (acces) => this.canUpdateAcces(acces),
		validClass: 'text-active',
		enabled: false,
	};
	private deleteAction: MassAction = {
		title: 'Supprimer l\'accès',
		action: () => this.doDelete(),
		class: 'fa fa-trash fa-lg',
		validation: (acces) => this.canDeleteAcces(acces),
		validClass: 'text-active',
		enabled: false,
	};
	
	// Variable contenant l'ensemble des actions de masse possibles
	allowedMassActions!: MassAction[];
	
	constructor(
		@Inject(JSR310ConversionService) jsr310Service: JSR310ConversionService,
		@Inject(EventBusService) eventBusService: EventBusService,
		@Inject(AccesService) accesService: AccesService,
	) {
		super(
			jsr310Service,
			eventBusService,
			accesService,
		);
		
		this.etat$.subscribe(
			etat => {
				// Evol 56004 - affichage des boutons d'action en fonction de l'etat presente
				switch(etat) {
					case AccesEtat.PLANIFIE:
						this.allowedMassActions = [
							this.authorizeAction,
							this.askValidationAction,
							this.denyAction,
							this.updateAction,
							this.deleteAction,
						];
						break;
					case AccesEtat.EN_COURS:
						this.allowedMassActions = [
							this.closeAction,
							this.updateAction,
						];
						break;
					default:
						this.allowedMassActions = [];
						break;				
				}
			}
		);

		this.refreshCommandsSubject.subscribe(
			(data) => {
				this.allSelected = false;
				this.selected = {};
				this.executionClicked = {};
				data.searchResults.forEach(
					(acces) => this.selected[acces.id] = false
				)

				this.allowedMassActions.forEach(action => action.enabled = false);
			}
		);
	}
	
	protected override evaluateDisplayedColumns(user: UserTLR, etat: AccesEtat): string[] {
		let displayedColumns = super.evaluateDisplayedColumns(user, etat);
		
		switch(etat) {
			case AccesEtat.PLANIFIE:
				// Evol 52316 - ajout de la colonne batch et de multi-selection pour les acces planifies
				displayedColumns = ['batch', ...displayedColumns, 'check'];
				break;
			case AccesEtat.EN_COURS:
				// Evol 56004 - ajout de la colonne batch et de multi-selection pour les acces en cours
				displayedColumns = ['batch', ...displayedColumns, 'check'];
				break;
			default:
				break;				
		}
		return displayedColumns;
	}
	
	onAllSelectedChange(allSelected: boolean) {
		if (this.allSelected != allSelected) {
			this.allSelected = allSelected;
			this.dataSource.data.forEach((acces) => this.selected[acces.id] = allSelected);
			
			this.updateButtons();
		}
	}
	
	onAccesSelectedChange(accesId: number, selected: boolean) {
		if (this.selected[accesId] != selected) {
			this.selected[accesId] = selected;
			if (selected) {
				this.allSelected = this.dataSource.data.every((acces) => this.selected[acces.id]);
			} else {
				this.allSelected = false;
			}

			this.updateButtons();
		}
	}
	
	private getSelectedAcces(): AccesSearchResult[] {
		return this.dataSource.data.filter((acces) => this.selected[acces.id]);
	} 
	
	private updateButtons() {
		let selectedAcces = this.getSelectedAcces();
		
		this.allowedMassActions.forEach(action => action.enabled = false);
		
		if (selectedAcces.length === 0) {
			return;
		}
		
		// L'utilisateur n'a acces aux boutons que si toutes les selections ont ete cree par le meme batch
		let jobExecutionIds = selectedAcces.map($ => $.jobExecutionId);
		let distinctJobsExecutionIds = jobExecutionIds.filter((item, index, source) => source.findIndex($ => $ === item) === index);
		if (distinctJobsExecutionIds.length > 1) {
			return;
		}

		this.allowedMassActions.forEach(action => {
			action.enabled = selectedAcces.every(acces => action.validation(acces));
		});
	}
	
	canAuthorizeAcces(data: AccesSearchResult): boolean {
		return (data.etat === 'ETAT_ENREGISTRE' || data.etat === 'ETAT_DEMANDE_ENTREE') && 
			(this.user.id === data.cacId || (this.user.id === data.ccId && data.typeAcces === 'A_DISTANCE')) 
			&& !data.entrepriseSeuls && this.autorisationPossible(data);
	}

	doAuthorize() {
		let selectedAcces = this.getSelectedAcces();	
		this.requestAction(AccesAction.AUTORISER, selectedAcces, 'mass');		
	}
	
	canAskValidationAcces(data: AccesSearchResult): boolean {
		return data.etat === 'ETAT_ENREGISTRE' && (this.user.id !== data.cacId && (this.user.id !== data.ccId || data.typeAcces !== 'A_DISTANCE'));
	}
	
	doAskValidation() {
		let selectedAcces = this.getSelectedAcces();	
		this.requestAction(AccesAction.DEMANDE_VALIDATION, selectedAcces);
	}
	
	canDenyAcces(data: AccesSearchResult): boolean {
		return (data.etat === 'ETAT_ENREGISTRE' || data.etat === 'ETAT_DEMANDE_ENTREE') && 
		(this.user.id === data.cacId || (this.user.id === data.ccId && data.typeAcces === 'A_DISTANCE')) && 
		!data.entrepriseSeuls && this.refusPossible(data);
	}
	
	doDeny() {
		let selectedAcces = this.getSelectedAcces();	
		this.requestAction(AccesAction.REFUSER, selectedAcces);
	}
	
	canCloseAcces(acces: AccesSearchResult): boolean {
		return acces.etat === 'ETAT_EN_COURS' &&
		(this.user.id === acces.cacId || (this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE')) &&
		!acces.etatDemandeFermeture;
	}
		
	doClose() {
		let selectedAcces = this.getSelectedAcces();	
		this.requestAction(AccesAction.FERMER, selectedAcces, 'mass');
	}
	
	canUpdateAcces(data: AccesSearchResult): boolean {
		return data.etat !== 'ETAT_TERMINE' && super.canUpdate(data);
	}
	
	doUpdate() {
		let selectedAcces = this.getSelectedAcces();
		this.requestAction(AccesAction.MODIFIER, selectedAcces);
	}
	
	canDeleteAcces(data: AccesSearchResult): boolean {
		return super.canDelete(data);
	}
	
	doDelete() {
		let selectedAcces = this.getSelectedAcces();
		this.requestAction(AccesAction.SUPPRIMER, selectedAcces);
	}
	
	// Ticket 54418
	executionIdClicked(acces: AccesSearchResult) {
		// Memoriser si on clique sur l'id de batch pour la premiere fois ou pas
		let clickedPreviously = this.executionClicked[acces.id];
		this.executionClicked[acces.id] = !clickedPreviously;
		
		// Selectionner ou deselectionner les acces correspondants a cet id
		// en fonction de cela
		let change = false;
		this.dataSource.data.forEach(
			($) => {
				if ($.jobExecutionId === acces.jobExecutionId) {
					this.selected[$.id] = !clickedPreviously;
					change = true;
				}
			}
		);
		
		// Mettre a jour la checkbox de selection totale si necessaire
		if (change) {
			if (clickedPreviously) {
				this.allSelected = false;
			} else {
				this.allSelected = this.dataSource.data.every((acces) => this.selected[acces.id]);
			}
		} 

		// Mettre a jour les boutons d'action
		this.updateButtons();
	}
}